import React from 'react';
import { useParams } from 'react-router-dom';

import {
  ArrowDownTrayIcon,
  ArrowTopRightOnSquareIcon,
  PaperClipIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { type ColumnDef } from '@oysterjs/ui/data-table';

import {
  ClientFileFieldsFragment,
  useGetClientFilesQuery,
  useRemoveClientFileMutation
} from '~/api/graphql';
import { type RowAction, RowActionsMenu } from '~/components/row-actions-menu';
import {
  activeFiltersToClientFileFilter,
  clientDocumentSortInput,
  createClientFileFilterDefinitions,
  type ClientDocumentSortKey
} from '~/api/data/clients/client-document-filters';
import { LinkDocumentDialog } from '~/components/clients/client-document-picker';
import {
  CLIENT_TAB_QUERY_OPTIONS,
  ClientListTab,
  removeConfirmCopy,
  removeFailed,
  useClientConnection
} from '~/components/clients/client-list-tab';
import {
  ClientTabRoute,
  eachOf,
  selectionColumn,
  useClientListControls
} from '~/components/clients/client-panels';
import { clientFilesVariables } from '~/api/data/clients/variables';
import { humanizeEnum } from '~/components/clients/clients-columns';
import { dateFormatter } from '~/components/table-formatters';
import { type DocumentFilter } from '~/api/data/documents/documents-filters';
import { downloadFile } from '~/components/documents/documents.utils';
import { ArchiveConfirmDialog } from '~/components/list/archive-confirm-dialog';
import { FloatingSelectionBar } from '~/components/list/floating-selection-bar';
import { formatFileSize } from '~/components/cara-input/cara-attachment';

type ClientFile = ClientFileFieldsFragment;

/** url is null for a file the caller may not read; the row still lists it, with removal alone. */
export const fileRowActions = (
  file: Pick<ClientFile, 'id' | 'name' | 'url'>,
  onRemove: (fileId: string) => void
): RowAction[] => {
  const url = file.url;
  return [
    ...(url
      ? [
          { label: 'Open file', icon: ArrowTopRightOnSquareIcon, href: url, target: '_blank' },
          {
            label: 'Download',
            icon: ArrowDownTrayIcon,
            onSelect: () => downloadFile(url, file.name)
          }
        ]
      : []),
    { label: 'Remove from client', icon: XMarkIcon, onSelect: () => onRemove(file.id) }
  ];
};

const dataColumns = (
  onRemove: (fileId: string) => void
): ColumnDef<ClientFile, ClientDocumentSortKey>[] => [
  {
    id: 'name',
    header: 'File name',
    sortKey: 'name',
    renderCell: (file) => (
      <span className="flex items-center gap-1.5 font-medium">
        <PaperClipIcon className="size-4 shrink-0 text-neutral-400 dark:text-neutral-500" />
        {file.name}
      </span>
    )
  },
  {
    id: 'type',
    header: 'Type',
    renderCell: (file) => humanizeEnum(file.type)
  },
  {
    id: 'size',
    header: 'Size',
    renderCell: (file) => formatFileSize(file.size)
  },
  {
    id: 'added',
    header: 'Added',
    sortKey: 'date',
    renderCell: (file) => dateFormatter.format(new Date(file.createdAt))
  },
  {
    id: 'actions',
    header: '',
    noHref: true,
    cellClassName: 'w-0 py-1.5 px-3',
    renderCell: (file) => (
      <RowActionsMenu label={`Actions for ${file.name}`} actions={fileRowActions(file, onRemove)} />
    )
  }
];

const ClientUploadsTable = ({ clientId }: { clientId: string }) => {
  const filterDefinitions = React.useMemo(() => createClientFileFilterDefinitions(), []);
  const { activeFilters, setActiveFilters, sortBy, sortDirection, setSort } = useClientListControls<
    DocumentFilter,
    ClientDocumentSortKey
  >('date');

  const variables = React.useMemo(
    () =>
      clientFilesVariables(
        clientId,
        activeFiltersToClientFileFilter(activeFilters),
        clientDocumentSortInput(sortBy, sortDirection)
      ),
    [activeFilters, clientId, sortBy, sortDirection]
  );

  const query = useGetClientFilesQuery({ variables, ...CLIENT_TAB_QUERY_OPTIONS });
  const list = useClientConnection(query, query.data?.client?.files, variables, {
    owner: { __typename: 'Client', id: clientId },
    fieldName: 'files'
  });
  const { selected, setSelected, selectedSet, bulk } = list;

  const [attaching, setAttaching] = React.useState(false);
  const [removing, setRemoving] = React.useState<string[] | null>(null);
  const [removeFile] = useRemoveClientFileMutation();

  const runRemove = async () => {
    if (!removing) return;
    const done = await bulk.run(
      'remove',
      () => eachOf(removing, (fileId) => removeFile({ variables: { clientId, fileId } })),
      removeFailed('file')
    );
    setRemoving(null);
    if (done) setSelected([]);
  };

  const columns = React.useMemo(
    () => [
      selectionColumn<ClientFile, ClientDocumentSortKey>(
        list.rows,
        selectedSet,
        setSelected,
        (file) => file.name
      ),
      ...dataColumns((id) => setRemoving([id]))
    ],
    [list.rows, selectedSet, setSelected]
  );

  return (
    <ClientListTab
      list={list}
      noun="files"
      filterDefinitions={filterDefinitions}
      onFiltersChange={setActiveFilters}
      filtered={activeFilters.length > 0}
      placeholder="Filter by date, or search..."
      action={{ label: 'Attach file', onClick: () => setAttaching(true) }}
      empty={{
        icon: PaperClipIcon,
        title: 'No files yet',
        description:
          'Policies, applications and loss runs uploaded for this client are collected here.'
      }}
      columns={columns}
      sortBy={sortBy}
      sortDirection={sortDirection}
      onSort={setSort}
    >
      {/* A file has no archive of its own, so removal takes the primary slot. */}
      <FloatingSelectionBar
        count={selected.length}
        onClear={() => setSelected([])}
        onArchive={() => setRemoving(selected)}
        isArchiving={bulk.running === 'remove'}
        archiveLabel="Remove from client"
        archiveIcon={<XMarkIcon className="size-3.5" />}
      />

      <ArchiveConfirmDialog
        open={removing !== null}
        count={removing?.length ?? 0}
        itemNoun="file"
        {...removeConfirmCopy(
          'file',
          removing?.length ?? 0,
          'They stay in the workspaces they were uploaded to.'
        )}
        loading={bulk.running !== null}
        onCancel={() => setRemoving(null)}
        onConfirm={runRemove}
      />

      <LinkDocumentDialog
        open={attaching}
        clientId={clientId}
        kind="file"
        onClose={() => setAttaching(false)}
        onAttached={list.refresh}
      />
    </ClientListTab>
  );
};

/** The Uploads tab: files people uploaded for this client, across its workspaces. */
export const ClientUploadsTab = () => {
  const { id = '' } = useParams();
  return <ClientTabRoute noun="files">{() => <ClientUploadsTable clientId={id} />}</ClientTabRoute>;
};
